import marketsMarketData from 'markets-market-data';
import { ERROR_TYPES, getErrorMessage } from './error-types';
import utils from '../utils';
import tracking from '../tracking';

const { HeapClient, Tracker, getHeapIoAppIdByEnv } = tracking;

export default {
    name: 'mwc-markets-component-mixin',
    props: {
        sessionId: {
            type: String
        },
        dataType: {
            type: String
        },
        skin: {
            type: String,
            default: ''
        },
        errorCode: {
            type: String,
            default: ''
        },
        targetElement: {
            type: HTMLElement
        }
    },
    data() {
        return {
            error: {}
        };
    },
    watch: {
        targetElement() {
            this.changeTargetElement();
        },
        sessionId(value) {
            this.tokenExpiredCallback({
                sessionId: value
            });
        },
        accessToken(value) {
            this.tokenExpiredCallback({
                accessToken: value
            });
        },
        dataType(value) {
            this.changeDataType(value);
        }
    },
    computed: {
        option() {
            const option = {};
            [
                'env',
                'instid',
                'sessionId',
                'accessToken',
                'frequency',
                'overridePaths'
            ].forEach(key => {
                if (
                    this.settings &&
                    typeof this.settings[key] !== 'undefined'
                ) {
                    option[key] = this.settings[key];
                }
            });
            // get the authentication info from loader if they are not passed through component settings
            const { morningstar } = window;
            if (
                morningstar &&
                morningstar.loader &&
                morningstar.loader.context
            ) {
                const {
                    getRuntimeEnvironment,
                    getApiToken,
                    getInstId
                } = morningstar.loader.context;
                if (!option.env) {
                    option.env = getRuntimeEnvironment();
                }
                if (!option.instid) {
                    option.instid = getInstId();
                }
                if (!option.sessionId) {
                    option.sessionId = getApiToken('marketsApiToken');
                    if (!option.accessToken) {
                        option.accessToken = getApiToken('apiGatewayToken');
                    }
                }
            }
            // some one will pass the whole access token with Bearer, then we need remove it to avoid duplicate
            if (option.accessToken) {
                option.accessToken = option.accessToken.replace('Bearer ', '');
            }
            return option;
        },
        errorMessage() {
            let msg = '';
            const errorCode = this.errorCode || this.error.code;
            if (errorCode) {
                msg =
                    this.initedLabels[errorCode] || getErrorMessage(errorCode);
            }
            return msg || this.error.message || '';
        }
    },
    beforeCreate() {
        this.ERROR_TYPES = ERROR_TYPES;
    },
    created() {
        if (marketsMarketData) {
            try {
                this.mktdata = marketsMarketData.setup(this.option);
                this.mktdata.track({
                    mwcVersion: '0.56.0',
                    productType:
                        this.settings && this.settings.productType
                            ? this.settings.productType
                            : 'mwc',
                    componentType: this.defaultConfig.intlNamespace,
                    clientDomain: `${window.location.protocol}//${window.location.hostname}`
                });
            } catch (e) {
                this._log('ERROR', `[MarketData] ${e.message}`);
                if (typeof this._setError === 'function') {
                    this._setError({
                        code: 'tokenRequired'
                    });
                }
                return;
            }
        } else {
            this._log(
                'ERROR',
                '[setupMarketData] marketsMarketData is undefined, please make sure module[markets-market-data] is imported.'
            );
            if (typeof this._setError === 'function') {
                this._setError({
                    code: 'dependencyRequired'
                });
            }
            return;
        }
        this.subscribeList = [];
        this.subscribeListener = this._subscribeListener();
        this.currentDataType = null;
        this.cachedData = {};
        this.addTokenExpiredCallback();
        this.tracker = new Tracker({
            scope:
                this.settings.trackingScope || this.defaultConfig.intlNamespace,
            client: HeapClient.getInstance(
                getHeapIoAppIdByEnv(this.option.env)
            ),
            // This represent only when the disableMwcMarketsTracking is strict true value, we close the tracking feature.
            isTracking: !!window.disableMwcMarketsTracking === false
        });
        this.mktdataReady();
    },
    destroyed() {
        this.$emit('destroyed');
    },
    methods: {
        changeDataType() {},
        changeTargetElement() {},
        addTokenExpiredCallback() {
            this.tokenExpiredCallback = function() {};
            this.mktdata.on('tokenExpired', (config, callback) => {
                this.tokenExpiredCallback = callback;
                this.$emit('token-expired', config);
                const apiTokenExpiredCallbackFromLoader = this.getApiTokenExpiredCallbackFromLoader();
                if (typeof apiTokenExpiredCallbackFromLoader === 'function') {
                    apiTokenExpiredCallbackFromLoader(
                        {
                            token: {
                                name: config.sessionId
                                    ? 'marketsApiToken'
                                    : 'apiGatewayToken'
                            }
                        },
                        (tokenName, tokenValue) => {
                            let key;
                            if (tokenName === 'apiGatewayToken') {
                                key = 'accessToken';
                            } else if (tokenName === 'marketsApiToken') {
                                key = 'sessionId';
                            }
                            if (key && tokenValue) {
                                const param = {};
                                param[key] = tokenValue;
                                this.tokenExpiredCallback(param);
                            }
                        }
                    );
                }
            });
        },
        mktdataReady() {
            this.mktdata.ready().then(() => {
                const { instid, userId } = this.mktdata.getOption();
                if (userId && instid) {
                    this.tracker.setUserId({
                        userId
                    });
                    this.tracker.setInstId({
                        instId: instid
                    });
                } else {
                    this._log(
                        'ERROR',
                        '[setupTracker] set userId and instId is failed, please check your account permission.'
                    );
                }
            });
        },
        getApiTokenExpiredCallbackFromLoader() {
            // get the apiTokenExpired callback from asterix.
            // todo: request EC core team to add this into the loader scope
            const { morningstar } = window;
            if (
                morningstar &&
                morningstar.asterix &&
                morningstar.asterix.context
            ) {
                const {
                    getApiTokenExpiredCallback
                } = morningstar.asterix.context;
                if (
                    utils.isFunction(getApiTokenExpiredCallback) &&
                    utils.isFunction(getApiTokenExpiredCallback())
                ) {
                    return getApiTokenExpiredCallback();
                }
            }
            return null;
        },
        hideSettingsPopover() {
            this.$emit('settings-popover-hide');
        },
        changeSettings(settings) {
            this.$emit('settings-changed', settings);
        },
        exposeMethods() {
            return ['toggleSettings'];
        },
        mergeLabels(labels) {
            const finalLabels = {};
            Object.keys(labels.en).forEach(labelKey => {
                finalLabels[labelKey] = this.labeller.getLabel(labelKey);
            });
            return finalLabels;
        },
        toggleSettings(targetElement) {
            if (this.$refs.UI && this.$refs.UI.toggleSettings) {
                this.$refs.UI.toggleSettings(targetElement);
            }
        },
        getDisplaySymbol(security = {}, withMICCode = true) {
            const symbol =
                security.clientSymbol ||
                security.mstarSymbol ||
                security.rtSymbol ||
                '–';
            let exch = '';
            if (withMICCode && security.MICCode) {
                exch = `${security.MICCode}:`;
            }
            return `${exch}${symbol}`;
        },
        exposeSecurity(symbol) {
            return this.mktdata.securities([symbol]).then(securities => {
                const security = securities[0];
                return {
                    name: security.name,
                    instrument: security.quoteInstrument,
                    currency: security.currency,
                    performanceId: security.performanceId,
                    MICCode: security.MICCode,
                    mstarSymbol: security.mstarSymbol,
                    Exch: security.Exch
                };
            });
        },
        _subscribeListener() {
            return {
                data: (security, updates) => {
                    if (typeof this.updateData === 'function') {
                        this.updateData(security, updates);
                    }
                },
                error: e => {}
            };
        },
        throttleUpdateData(security, updates, updateFunc, delay) {
            if (this.currentDataType === 'pull') {
                updateFunc.apply(this, [security, updates]);
                return;
            }
            if (!this.throttleUpdate) {
                this.throttleUpdate = utils.throttle(
                    this._updateCacheData,
                    delay,
                    {
                        leading: false
                    }
                );
            }
            this._setCacheData(security, updates);
            this.throttleUpdate(updateFunc);
        },
        _accptedTicker(ticker) {
            this.exposeSecurity(ticker).then(securityInfo => {
                this.$emit('ticker-accepted', securityInfo);
            });
        },
        _setCacheData(security, updates) {
            if (!this.cachedData[security]) {
                this.cachedData[security] = updates;
            } else {
                const cacheData = this.cachedData[security];
                updates.forEach(d => {
                    const index = utils.inArray(cacheData, function(item) {
                        return item.name === d.name;
                    });
                    if (index > -1) {
                        cacheData[index] = utils.extend(true, {}, d);
                    } else {
                        cacheData.push(d);
                    }
                });
            }
        },
        _updateCacheData(updateFunc) {
            updateFunc.apply(this, [
                Object.keys(this.cachedData),
                Object.values(this.cachedData)
            ]);
            this.cachedData = {};
        },
        getSALPaths({ service, bundle } = {}) {
            const env = this.settings.env.toLowerCase();
            if (!service) {
                switch (env) {
                    case 'dev':
                    case 'qa':
                    case 'stg':
                        service = 'https://api-stg.morningstar.com';
                        break;
                    default:
                        service = 'https://api-global.morningstar.com';
                        break;
                }
            }
            if (!bundle) {
                switch (env) {
                    case 'dev':
                    case 'qa':
                    case 'stg':
                        bundle = 'https://markets-uat.morningstar.com/';
                        break;
                    default:
                        bundle = 'https://quotespeed.morningstar.com/';
                        break;
                }
            }
            return {
                service,
                bundle
            };
        },
        _log(type, message, stackTrace) {
            type = type ? type.toLowerCase() : 'log';
            if (this.logger && this.logger[type] && this.mwcId && message) {
                this.logger[type](this.mwcId, message, stackTrace);
            }
        }
    }
};
